import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Linkedin, Loader2, CheckCircle2, AlertCircle, Send, Link2Off } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LinkedInConnect({ getHeaders }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/linkedin/status`, { headers: getHeaders() });
      const d = await res.json();
      setStatus(d);
    } catch (e) { setStatus({ connected: false }); }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  // Surface ?linkedin_connected / ?linkedin_error from the OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('linkedin_connected')) {
      setNotice({ type: 'ok', msg: 'LinkedIn connected successfully.' });
      window.history.replaceState({}, '', '/client/dashboard');
    } else if (params.get('linkedin_error')) {
      setNotice({ type: 'err', msg: `LinkedIn connection failed: ${params.get('linkedin_error')}` });
      window.history.replaceState({}, '', '/client/dashboard');
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/linkedin/connect`, { headers: getHeaders() });
      const d = await res.json();
      if (d.auth_url) { window.location.href = d.auth_url; return; }
      setNotice({ type: 'err', msg: d.detail || 'Could not start LinkedIn connection.' });
    } catch (e) {
      setNotice({ type: 'err', msg: 'Could not start LinkedIn connection.' });
    }
    setConnecting(false);
  };

  const selectOrg = async (urn) => {
    try {
      await fetch(`${API}/api/client/dashboard/linkedin/select-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ org_urn: urn }),
      });
      load();
    } catch (e) { /* noop */ }
  };

  const disconnect = async () => {
    await fetch(`${API}/api/client/dashboard/linkedin/disconnect`, { method: 'POST', headers: getHeaders() });
    load();
  };

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    setNotice(null);
    try {
      const res = await fetch(`${API}/api/client/dashboard/linkedin/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ text: text.trim() }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setNotice({ type: 'ok', msg: 'Posted to LinkedIn!' });
        setText('');
      } else {
        setNotice({ type: 'err', msg: d.detail || 'Post failed.' });
      }
    } catch (err) {
      setNotice({ type: 'err', msg: 'Post failed.' });
    }
    setPosting(false);
  };

  const orgs = status?.organizations || [];
  const selected = status?.selected_org;

  return (
    <Card className="border-gray-200" data-testid="linkedin-connect-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#0a66c220' }}>
            <Linkedin className="h-5 w-5" style={{ color: '#0a66c2' }} />
          </div>
          <div className="flex-1">
            <p className="text-gray-900 text-sm font-semibold">LinkedIn Company Page</p>
            <p className="text-[11px] text-gray-500">Connect your LinkedIn Page to publish posts.</p>
          </div>
          {status?.connected
            ? <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>
            : <Badge className="bg-gray-100 text-gray-500 text-xs">Not connected</Badge>}
        </div>

        {notice && (
          <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-3 ${notice.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="linkedin-notice">
            {notice.type === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {notice.msg}
          </div>
        )}

        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : !status?.connected ? (
          <Button onClick={connect} disabled={connecting} style={{ backgroundColor: '#0a66c2' }} className="text-white" data-testid="linkedin-connect-btn">
            {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting...</> : <><Linkedin className="h-4 w-4 mr-2" /> Connect LinkedIn</>}
          </Button>
        ) : (
          <div className="space-y-4">
            {orgs.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1.5">Company Page</p>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={selected?.urn || ''}
                  onChange={(e) => selectOrg(e.target.value)}
                  data-testid="linkedin-org-select"
                >
                  {orgs.map((o) => <option key={o.urn} value={o.urn}>{o.name || o.urn}</option>)}
                </select>
              </div>
            ) : (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg" data-testid="linkedin-no-orgs">
                No Company Pages found for this LinkedIn account. Make sure you are an admin of a Page.
              </p>
            )}

            <form onSubmit={post} className="space-y-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a post for your LinkedIn Company Page..."
                rows={4}
                data-testid="linkedin-post-text"
              />
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={posting || !text.trim()} style={{ backgroundColor: '#0a66c2' }} className="text-white" data-testid="linkedin-post-btn">
                  {posting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting...</> : <><Send className="h-4 w-4 mr-2" /> Post</>}
                </Button>
                <Button type="button" variant="ghost" onClick={disconnect} className="text-gray-400 hover:text-red-500" data-testid="linkedin-disconnect-btn">
                  <Link2Off className="h-4 w-4 mr-1" /> Disconnect
                </Button>
              </div>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
